import { create } from 'zustand';
import type { AuthBrief, AuthMembership, AuthProgram, AuthUser, LoginSuccess } from '../api/auth.js';

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  tenant: AuthMembership | null;
  programs: AuthProgram[];
  availableTenants: AuthBrief[];
  /** True si la suscripción OPUS 27 venció. m0 entra en modo lectura. */
  subscriptionExpired: boolean;
  /** Fecha de caducidad (ISO) si Anywhere la reportó. */
  subscriptionExpiresAt: string | null;
  /** True sólo en entornos de pruebas para admin/owner ECOSOFT. */
  qaModeEnabled: boolean;
  /** Si esta persona ve la actividad de usuarios. Lo decide el servidor. */
  puedeVerActividad: boolean;
  /** Sitio demo público (otra URL): solo lectura. */
  demoGuest: boolean;
  /** CTA producto real (demo). */
  demoProductUrl: string | null;

  setSession: (s: LoginSuccess) => void;
  clearSession: () => void;
  setUnknown: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  tenant: null,
  programs: [],
  availableTenants: [],
  subscriptionExpired: false,
  subscriptionExpiresAt: null,
  qaModeEnabled: false,
  puedeVerActividad: false,
  demoGuest: false,
  demoProductUrl: null,

  setSession: (s) =>
    set({
      status: 'authenticated',
      user: s.user,
      tenant: s.tenant,
      programs: s.programs,
      availableTenants: s.availableTenants ?? [],
      subscriptionExpired: s.subscriptionExpired ?? false,
      subscriptionExpiresAt: s.subscriptionExpiresAt ?? null,
      qaModeEnabled: s.qaModeEnabled ?? false,
      puedeVerActividad: s.puedeVerActividad === true,
      demoGuest: s.demoGuest === true,
      demoProductUrl: s.productUrl ?? null,
    }),

  clearSession: () =>
    set({
      status: 'unauthenticated',
      user: null,
      tenant: null,
      programs: [],
      availableTenants: [],
      subscriptionExpired: false,
      subscriptionExpiresAt: null,
      qaModeEnabled: false,
      puedeVerActividad: false,
      demoGuest: false,
      demoProductUrl: null,
    }),

  setUnknown: () => set({ status: 'unknown' }),
}));

export function hasProgram(programId: string): boolean {
  return useAuthStore.getState().programs.some((p) => p.programId === programId);
}

/** Hook React: ¿el usuario actual es de solo consulta (role=viewer)? */
export function useIsViewer(): boolean {
  return useAuthStore((s) => s.tenant?.role === 'viewer');
}

/** Hook React: ¿el usuario puede mutar (cualquier role excepto viewer)?
 *  También retorna `false` si la suscripción OPUS 27 venció — m0 entra
 *  en modo lectura hasta que se renueve. */
export function useCanMutate(): boolean {
  return useAuthStore((s) => {
    if (s.demoGuest) return false;
    if (s.subscriptionExpired) return false;
    const role = s.tenant?.role;
    return role === 'owner' || role === 'admin' || role === 'member';
  });
}

/** Hook: ¿estamos en sesión del sitio demo público? */
export function useIsDemoGuest(): boolean {
  return useAuthStore((s) => s.demoGuest);
}

/** Hook React: estado de la suscripción OPUS 27. */
export function useSubscriptionInfo(): { expired: boolean; expiresAt: string | null } {
  return useAuthStore((s) => ({
    expired: s.subscriptionExpired,
    expiresAt: s.subscriptionExpiresAt,
  }));
}

/** Hook React: ¿el usuario es admin/owner de la empresa actual?
 *  Usar para acciones globales (subir catálogo, eliminar catálogo,
 *  administrar miembros). */
export function useIsAdmin(): boolean {
  return useAuthStore((s) => s.tenant?.role === 'admin' || s.tenant?.role === 'owner');
}

/** Hook React: ¿es staff interno de Ecosoft (admin/owner del tenant Ecosoft)?
 *  Gate para info sensible que el CLIENTE no debe ver: costo $ de IA, modelo
 *  LLM usado, dashboards de consumo. Mismo criterio que las rutas /staff/*. */
/**
 * ¿Esta persona ve la actividad de usuarios? Lo decide el SERVIDOR y llega como un
 * booleano: la lista de correos autorizados vive solo en el API (`quienVeLaActividad.ts`)
 * y nunca viaja al navegador. Esconder el menú es cortesía, no seguridad — el portero de
 * verdad está en los dos endpoints.
 */
export function usePuedeVerActividad(): boolean {
  return useAuthStore((s) => s.puedeVerActividad);
}

export function useIsEcosoftStaff(): boolean {
  return useAuthStore(
    (s) => !!s.tenant?.isEcosoftOwned && (s.tenant.role === 'admin' || s.tenant.role === 'owner'),
  );
}
